/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';

import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';

/**
 * Master data export items.
 * Add a new entry here to expose another table on this page.
 *
 * Fields:
 *   key     – unique identifier (used as React key)
 *   table   – exact DB table name sent to the backend whitelist
 *   label   – Thai display name shown on the button card
 *   icon    – Material Icons name
 */
const MASTER_DATA_ITEMS = [
  { key: 'products', table: 'products', label: 'สินค้า', icon: 'inventory_2' },
  {
    key: 'products_barcode',
    table: 'products_barcode',
    label: 'บาร์โค้ดสินค้า',
    icon: 'inventory_2',
  },
  {
    key: 'products_level_category',
    table: 'products_level_category',
    label: 'หมวดสินค้า',
    icon: 'category',
  },
  { key: 'units', table: 'units', label: 'หน่วยนับ', icon: 'straighten' },
  { key: 'brands', table: 'brands', label: 'ยี่ห้อ', icon: 'branding_watermark' },
  { key: 'locations', table: 'locations', label: 'สถานที่', icon: 'location_on' },
  { key: 'warehouses', table: 'warehouses', label: 'คลังสินค้า', icon: 'warehouse' },
  // { key: 'sale_orders', table: 'sale_orders', label: 'บิลขาย', icon: 'shopping_cart' },
  // {
  //   key: 'sale_orders_list',
  //   table: 'sale_orders_list',
  //   label: 'รายการขายสินค้า',
  //   icon: 'shopping_cart',
  // },
  { key: 'target_plans', table: 'target_plans', label: 'เป้าหมายการขาย', icon: 'crisis_alert' },
];

/**
 * Convert an array of objects to a UTF-8 BOM CSV string.
 * BOM (\uFEFF) ensures Thai characters display correctly when opened in Excel.
 */
function toCSV(columns, rows) {
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const header = columns.map(escape).join(',');
  const body = rows.map((row) => columns.map((col) => escape(row[col])).join(','));
  return `\uFEFF${[header, ...body].join('\r\n')}`;
}

function downloadCSV(filename, csvString) {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function MasterData() {
  const mod = 'master_data';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [loadingKey, setLoadingKey] = useState(null);

  const handleExport = async (item) => {
    setLoadingKey(item.key);
    try {
      const res = await FetchData('POST', `/${mod}`, app.token, { table: item.table });
      if (res.status === 'ok' && res.rows) {
        const csv = toCSV(res.columns, res.rows);
        downloadCSV(`${item.table}.csv`, csv);
      } else {
        console.error('master_data export error:', res.msg);
      }
    } catch (err) {
      console.error('master_data export failed:', err);
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <DashboardLayout>
      <Navbar route={[t('menu.setting'), t('menu.master_data')]} />

      <MDBox py={3}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant='h5' fontWeight='medium' mb={1}>
              {t('menu.master_data')}
            </MDTypography>
            <MDTypography variant='body2' color='secondary' mb={3}>
              {t('master_data.description')}
            </MDTypography>

            <Grid container spacing={2}>
              {MASTER_DATA_ITEMS.map((item) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.key}>
                  <Card variant='outlined' sx={{ height: '100%' }}>
                    <MDBox
                      display='flex'
                      flexDirection='column'
                      alignItems='center'
                      justifyContent='center'
                      p={3}
                      gap={1}
                    >
                      <Icon fontSize='large' color='info'>
                        {item.icon}
                      </Icon>
                      <MDTypography variant='button' fontWeight='medium' textAlign='center'>
                        {item.label}
                      </MDTypography>
                      <MDTypography variant='caption' color='secondary' textAlign='center'>
                        {item.table}
                      </MDTypography>
                      <Tooltip title={`Export ${item.label} เป็น CSV`} arrow>
                        <span>
                          <MDButton
                            variant='gradient'
                            color='success'
                            size='small'
                            startIcon={
                              loadingKey === item.key ? (
                                <CircularProgress size={14} color='inherit' />
                              ) : (
                                <Icon>download</Icon>
                              )
                            }
                            disabled={loadingKey !== null}
                            onClick={() => handleExport(item)}
                          >
                            Export CSV
                          </MDButton>
                        </span>
                      </Tooltip>
                    </MDBox>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </MDBox>
        </Card>
      </MDBox>

      <Backdrop open={loadingKey !== null} sx={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color='inherit' />
      </Backdrop>

      <Footer />
    </DashboardLayout>
  );
}

export default MasterData;
